import React from 'react';

/**
 * Enhanced Timetable Parser for MyClass
 * Handles complex parsing of teacher timetable files with multi-class/teacher/room support
 */
export class EnhancedTimetableParser {
  constructor(setupData) {
    this.setupData = setupData;
    this.periodPatterns = setupData.periodPatterns || [];
  }

  /**
   * Parse teacher timetable file content with enhanced multi-parsing support
   * @param {string} content - File content
   * @returns {Object} Parsed timetable data
   */
  parseTimetableFile(content) {
    try {
      // Extract teacher name/code using robust regex - handle multiple formats
      let teacherMatch = content.match(/## Teacher \d+: (.+?) \(/);
      if (!teacherMatch) {
        // Try format without parentheses: ## Teacher X: Name
        teacherMatch = content.match(/## Teacher \d+: (.+?)(?:\n|$)/);
      }
      if (!teacherMatch) {
        // Try alternative format: **Teacher:** Name (Name)
        teacherMatch = content.match(/\*\*Teacher:\*\* (.+?) \(/);
      }
      if (!teacherMatch) {
        // Try format without parentheses: **Teacher:** Name
        teacherMatch = content.match(/\*\*Teacher:\*\* (.+?)(?:\n|$)/);
      }
      if (!teacherMatch) {
        // Try format: Teacher X: Name
        teacherMatch = content.match(/Teacher \d+: (.+?)(?:\n|$)/);
      }
      if (!teacherMatch) {
        // Try new format: Teacher: "Name" (Code: Code)
        teacherMatch = content.match(/Teacher: "(.+?)" \(Code: (.+?)\)/);
        if (teacherMatch) {
          // For new format, use the code instead of the name
          const teacherCode = teacherMatch[2].trim();
          console.log(`👨‍🏫 Teacher Code: ${teacherCode}`);
          return this.parseNewFormatTimetableFile(content, teacherCode);
        }
      }
      if (!teacherMatch) {
        throw new Error('Could not extract teacher name from any known format');
      }
      
      const teacherName = teacherMatch[1].trim();
      
      // Detect class roles (Class Master, Vice Class Master)
      const classRoles = this.detectClassRoles(content);
      console.log(`👨‍🏫 Teacher: ${teacherName}`);
      console.log(`🎭 Class Roles:`, classRoles);
      
      // Parse schedule by day - handle multiple formats
      const schedule = {};
      // Try different day header formats
      let daySections = content.split(/\*\*(Monday|Tuesday|Wednesday|Thursday|Friday):\*\*/);
      if (daySections.length === 1) {
        // Try format without ** around day names
        daySections = content.split(/\*\*(Monday|Tuesday|Wednesday|Thursday|Friday):\*\*/);
      }
      if (daySections.length === 1) {
        // Try format with just day names
        daySections = content.split(/(Monday|Tuesday|Wednesday|Thursday|Friday):/);
      }
      
      for (let i = 1; i < daySections.length; i += 2) {
        const dayName = daySections[i];
        const dayContent = daySections[i + 1];
        
        if (!dayContent) continue;
        
        const periods = this.parseDayContent(dayContent, dayName, classRoles, teacherName);
        if (periods.length > 0) {
          schedule[dayName] = periods;
        }
      }
      
      return {
        teacherName: teacherName,
        classRoles: classRoles,
        schedule: schedule
      };
    } catch (error) {
      console.error('Error parsing file:', error.message);
      throw error;
    }
  }

  /**
   * Parse new format timetable file with teacher code
   * @param {string} content - File content
   * @param {string} teacherCode - Teacher code
   * @returns {Object} Parsed timetable data
   */
  parseNewFormatTimetableFile(content, teacherCode) {
    try {
      console.log(`🔄 Parsing new format with teacher code: ${teacherCode}`);
      
      // Detect class roles (Class Master, Vice Class Master) with enhanced parsing
      const classRoles = this.detectClassRolesEnhanced(content);
      console.log(`🎭 Enhanced Class Roles:`, classRoles);
      
      // Parse schedule by day - handle Chinese day names
      const schedule = {};
      const daySections = content.split(/(星期一|星期二|星期三|星期四|星期五):/);
      
      for (let i = 1; i < daySections.length; i += 2) {
        const dayName = this.mapChineseDayToEnglish(daySections[i]);
        const dayContent = daySections[i + 1];
        
        if (!dayContent) continue;
        
        const periods = this.parseNewFormatDayContent(dayContent, dayName, classRoles, teacherCode);
        if (periods.length > 0) {
          schedule[dayName] = periods;
        }
      }
      
      return {
        teacherName: teacherCode, // Use code as the identifier
        teacherCode: teacherCode,
        classRoles: classRoles,
        schedule: schedule
      };
    } catch (error) {
      console.error('Error parsing new format file:', error.message);
      throw error;
    }
  }

  /**
   * Map Chinese day names to English
   * @param {string} chineseDay - Chinese day name
   * @returns {string} English day name
   */
  mapChineseDayToEnglish(chineseDay) {
    const dayMapping = {
      '星期一': 'Monday',
      '星期二': 'Tuesday', 
      '星期三': 'Wednesday',
      '星期四': 'Thursday',
      '星期五': 'Friday'
    };
    return dayMapping[chineseDay] || chineseDay;
  }

  /**
   * Parse new format day content with teacher code extraction
   * @param {string} dayContent - Day content from file
   * @param {string} dayName - Day name
   * @param {Object} classRoles - Class roles information
   * @param {string} defaultTeacherCode - Default teacher code from file
   * @returns {Array} Array of period data
   */
  parseNewFormatDayContent(dayContent, dayName, classRoles = null, defaultTeacherCode = null) {
    const periods = [];
    // Updated regex to match the new format: "- 第一節 08:10 - 08:45: 3D*綜合人文-<嘉>-G03"
    const periodMatches = dayContent.matchAll(/- 第[一二三四五六七八九十]+節\s+(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2}):\s*(.+)/g);
    
    for (const match of periodMatches) {
      const timeStart = match[1];
      const timeEnd = match[2];
      const description = match[3].trim();
      
      // Extract period number from the Chinese text
      const periodNumber = this.extractPeriodNumberFromChinese(match[0]);
      
      console.log(`📅 Parsing period ${periodNumber}: "${description}"`);
      
      // Get period info with debugging
      const periodInfo = this.getPeriodInfo(periodNumber, dayName);
      console.log(`📊 Period info for ${periodNumber}:`, periodInfo);
      
      // Parse the description to extract classes, subjects, teachers, and rooms
      const parsedEntries = this.parseNewFormatPeriodDescription(description, periodNumber, `${timeStart}-${timeEnd}`, dayName, defaultTeacherCode);
      periods.push(...parsedEntries);
    }
    
    // Add 班主任節 periods for Class Master and Vice Class Master with enhanced assumptions
    if (classRoles && (classRoles.isClassMaster || classRoles.isViceClassMaster)) {
      console.log(`🎭 Adding 班主任節 periods for ${classRoles.isClassMaster ? 'Class Master' : 'Vice Class Master'}`);
      
      // Use the assigned class as class_id and home room for 班主任節 periods
      const assignedClass = classRoles.classes.length > 0 ? classRoles.classes[0] : 'Unknown Class';
      const homeRoom = classRoles.homeRoom || 'Unknown Room';
      
      // Add period_sequence=1 (first 班主任節)
      const firstHomeroomPeriod = {
        period: 0, // Special period
        period_sequence: 1,
        period_node: '班主任節',
        timeRange: '8:00-8:10',
        classes: [assignedClass], // Use assigned class as class_id
        subject: '班主任節',
        rooms: [homeRoom], // Use home room
        teachers: [defaultTeacherCode],
        type: 'admin', // Mark as admin type for special styling
        subject_type: 'admin',
        isClassMaster: classRoles.isClassMaster,
        isViceClassMaster: classRoles.isViceClassMaster
      };
      periods.unshift(firstHomeroomPeriod); // Add to beginning
      
      // Add period_sequence=15 (last 班主任節)
      const lastHomeroomPeriod = {
        period: 0, // Special period
        period_sequence: 15,
        period_node: '班主任節',
        timeRange: '3:35-3:50',
        classes: [assignedClass], // Use assigned class as class_id
        subject: '班主任節',
        rooms: [homeRoom], // Use home room
        teachers: [defaultTeacherCode],
        type: 'admin', // Mark as admin type for special styling
        subject_type: 'admin',
        isClassMaster: classRoles.isClassMaster,
        isViceClassMaster: classRoles.isViceClassMaster
      };
      periods.push(lastHomeroomPeriod); // Add to end
      
      console.log(`✅ Added 班主任節 periods: sequence 1 and 15 with class_id="${assignedClass}" and room="${homeRoom}"`);
    }
    
    return periods;
  }

  /**
   * Extract period number from Chinese period text
   * @param {string} chinesePeriodText - Chinese period text like "第一節"
   * @returns {number} Period number
   */
  extractPeriodNumberFromChinese(chinesePeriodText) {
    const chineseNumbers = {
      '一': 1, '二': 2, '三': 3, '四': 4, '五': 5,
      '六': 6, '七': 7, '八': 8, '九': 9, '十': 10
    };
    
    const match = chinesePeriodText.match(/第([一二三四五六七八九十]+)節/);
    if (match) {
      const chineseNum = match[1];
      return chineseNumbers[chineseNum] || 1;
    }
    
    return 1; // Default fallback
  }

  /**
   * Parse new format period description with enhanced context legend support
   * @param {string} description - Period description
   * @param {number} periodNumber - Period number
   * @param {string} timeRange - Time range
   * @param {string} dayName - Day name
   * @param {string} defaultTeacherCode - Default teacher code from file
   * @returns {Array} Array of parsed entries
   */
  parseNewFormatPeriodDescription(description, periodNumber, timeRange, dayName = null, defaultTeacherCode = null) {
    const entries = [];
    
    // Get period sequence and node from setup data
    const periodInfo = this.getPeriodInfo(periodNumber, dayName);
    
    console.log(`Parsing new format description: "${description}"`);
    
    // Check for empty periods first - these should be skipped entirely
    if (!description || description.trim() === '' || description.trim() === '""') {
      console.log(`⏭️ Empty period detected - skipping period ${periodNumber} (no data to save)`);
      return entries; // Return empty array - no entries to create
    }
    
    // FLEXIBLE RULE: Check if this period is "barred" (restricted) by settings
    const isBarredPeriod = this.isBarredPeriodBySettings(periodInfo.period_sequence, periodInfo.period_node);
    
    if (isBarredPeriod) {
      // This period is "barred" - only allow access if it matches settings exactly
      const validatedDescription = this.validateBarredPeriodDescription(description, periodInfo.period_node);
      
      entries.push({
        period: periodNumber,
        period_sequence: periodInfo.period_sequence,
        period_node: periodInfo.period_node,
        timeRange: timeRange,
        classes: [],
        subject: validatedDescription,
        rooms: [],
        teachers: [],
        type: 'special'
      });
      return entries;
    }
    
    // Parse the enhanced new format: "class_id*subject-teacher_name/teacher_name-classroom"
    // This handles the context legend format you provided
    // Updated regex to handle up to 4 co-teachers: <teacher1>/<teacher2>/<teacher3>/<teacher4>
    const enhancedFormatMatch = description.match(/^([^*]+)\*([^<]+)-<([^>]+)>(?:\/<([^>]+)>(?:\/<([^>]+)>(?:\/<([^>]+)>)?)?)?-(.+)$/);
    if (enhancedFormatMatch) {
      const classId = enhancedFormatMatch[1].trim();
      const subject = enhancedFormatMatch[2].trim();
      const teacherCode = enhancedFormatMatch[3].trim();
      const coTeacherCode1 = enhancedFormatMatch[4] ? enhancedFormatMatch[4].trim() : null;
      const coTeacherCode2 = enhancedFormatMatch[5] ? enhancedFormatMatch[5].trim() : null;
      const coTeacherCode3 = enhancedFormatMatch[6] ? enhancedFormatMatch[6].trim() : null;
      const classroom = enhancedFormatMatch[7].trim();
      
      // Skip periods with empty or placeholder content
      if (this.isEmptyOrPlaceholderContent(classId, subject, teacherCode, classroom)) {
        console.log(`⏭️ Placeholder/empty content detected - skipping period ${periodNumber}`);
        return entries; // Return empty array - no entries to create
      }
      
      console.log(`🔍 ENHANCED FORMAT: classId="${classId}", subject="${subject}", teacherCode="${teacherCode}", coTeacherCode1="${coTeacherCode1}", coTeacherCode2="${coTeacherCode2}", coTeacherCode3="${coTeacherCode3}", classroom="${classroom}"`);
      
      // Determine subject type based on class pattern
      const subjectType = this.determineSubjectType(classId, subject);
      console.log(`📊 Subject type determined: ${subjectType} for class "${classId}" subject "${subject}"`);
      
      // Handle multiple classrooms (e.g., "612/331")
      const classrooms = classroom.includes('/') ? classroom.split('/') : [classroom];
      
      // Collect all teacher codes
      const teacherCodes = [teacherCode];
      const coTeachers = [];
      if (coTeacherCode1) {
        teacherCodes.push(coTeacherCode1);
        coTeachers.push(coTeacherCode1);
      }
      if (coTeacherCode2) {
        teacherCodes.push(coTeacherCode2);
        coTeachers.push(coTeacherCode2);
      }
      if (coTeacherCode3) {
        teacherCodes.push(coTeacherCode3);
        coTeachers.push(coTeacherCode3);
      }
      
      // Create entries for each classroom
      for (let i = 0; i < classrooms.length; i++) {
        const currentClassroom = classrooms[i].trim();
        const currentTeacherCode = teacherCodes[i] || teacherCodes[0];
        
        const entry = {
          period: periodNumber,
          period_sequence: periodInfo.period_sequence,
          period_node: periodInfo.period_node,
          timeRange: timeRange,
          classes: [classId],
          subject: subject,
          rooms: [currentClassroom],
          teachers: [currentTeacherCode],
          type: subjectType,
          elective_group: this.extractElectiveGroup(classId, subject),
          co_teachers: coTeachers
        };
        
        console.log(`🔍 Created enhanced format entry:`, entry);
        entries.push(entry);
      }
      
      return entries;
    }
    
    // Fallback to old format parsing if enhanced format doesn't match
    console.log(`⚠️ Enhanced format not matched, falling back to old format parsing`);
    return this.parsePeriodDescription(description, periodNumber, timeRange, dayName, defaultTeacherCode);
  }

  /**
   * Determine subject type based on class ID pattern
   * @param {string} classId - Class ID (e.g., "5X2", "4AB3", "S5", "會議")
   * @returns {string} Subject type: "core", "elective", "admin", "special"
   */
  determineSubjectType(classId, subject = null) {
    if (!classId) return 'core';
    
    // Check subject name first for admin types
    if (subject && (subject === '班主任節' || subject.includes('班主任節'))) {
      return 'admin';
    }
    
    // Admin/Meeting types based on class ID
    if (classId === '會議' || classId === '同儕') {
      return 'admin';
    }
    
    // Elective types (X2 pattern) - like "5X2"
    if (classId.includes('X2')) {
      return 'elective';
    }
    
    // Form level (S1, S2, S3, S4, S5, S6) - core subjects
    if (classId.match(/^S[1-6]$/)) {
      return 'core';
    }
    
    // Combined classes (4AB3) - core subjects
    if (classId.match(/^\d+[A-Z]+\d+$/)) {
      return 'core';
    }
    
    // Regular classes (5D, 2C, etc.) - core subjects
    if (classId.match(/^\d+[A-Z]$/)) {
      return 'core';
    }
    
    // Default to core
    return 'core';
  }

  /**
   * Extract elective group from class ID and subject
   * @param {string} classId - Class ID
   * @param {string} subject - Subject name
   * @returns {string|null} Elective group or null
   */
  extractElectiveGroup(classId, subject) {
    // For X2 classes, extract the elective group from the subject
    if (classId.includes('X2')) {
      // Remove common suffixes like "(隔周)" from subject
      const cleanSubject = subject.replace(/\([^)]*\)$/, '').trim();
      return cleanSubject;
    }
    
    return null;
  }

  /**
   * Map teacher code to full name using saas_teachers table
   * This would typically make an API call to get the full name
   * @param {string} teacherCode - Teacher code (e.g., "章", "城", "藍")
   * @returns {Promise<string>} Full teacher name
   */
  async mapTeacherCodeToName(teacherCode) {
    // This is a placeholder - in a real implementation, you would:
    // 1. Make an API call to query the saas_teachers table
    // 2. Search for the teacher with the matching code
    // 3. Return the cname field
    
    // For now, return a mapping based on the sample data
    const teacherCodeMapping = {
      '章': '鄺潤章',
      '城': '梁瑋城', 
      '藍': '藍楚蕎',
      '任': '任康哲',
      'Naz': 'Naz Iraj',
      '恒': '劉銘恒',
      '范': '范丹雲'
    };
    
    return teacherCodeMapping[teacherCode] || teacherCode;
  }

  /**
   * Process teacher codes and convert them to full names
   * @param {Array} teacherCodes - Array of teacher codes
   * @returns {Promise<Array>} Array of full teacher names
   */
  async processTeacherCodes(teacherCodes) {
    const fullNames = [];
    for (const code of teacherCodes) {
      const fullName = await this.mapTeacherCodeToName(code);
      fullNames.push(fullName);
    }
    return fullNames;
  }

  /**
   * Map teacher code to full name synchronously
   * @param {string} teacherCode - Teacher code (e.g., "章", "城", "藍")
   * @returns {string} Full teacher name
   */
  mapTeacherCodeToNameSync(teacherCode) {
    // This is a placeholder - in a real implementation, you would:
    // 1. Query the saas_teachers table synchronously
    // 2. Search for the teacher with the matching code
    // 3. Return the cname field
    
    // For now, return a mapping based on the sample data
    const teacherCodeMapping = {
      '章': '鄺潤章',
      '城': '梁瑋城', 
      '藍': '藍楚蕎',
      '任': '任康哲',
      'Naz': 'Naz Iraj',
      '恒': '劉銘恒',
      '廣': '陳廣隆',
      '輝': '陳柏輝',
      '嘉': '陳嘉俊',
      '范': '范丹雲',
      '黎': '黎志偉',
      '聰': '李惠聰',
      '蘇': '蘇家輝',
      'Fiona': 'FIONA WANGARI',
      '孫': '孫瑞強',
      '曉': '吳曉敏'
    };
    
    return teacherCodeMapping[teacherCode] || teacherCode;
  }

  /**
   * Check if content represents empty or placeholder data that should be skipped
   * @param {string} classPart - Class part
   * @param {string} subject - Subject
   * @param {string} teacherCode - Teacher code
   * @param {string} room - Room
   * @returns {boolean} Whether this content should be skipped
   */
  isEmptyOrPlaceholderContent(classPart, subject, teacherCode, room) {
    // Check for empty or placeholder values
    const emptyValues = ['', '""', '""', 'Unknown', 'Unknown Room', 'N/A', '核心科'];
    
    // TBC (To Be Confirmed) is valid for admin/meeting types
    const adminTypes = ['會議', '同儕'];
    const isAdminType = adminTypes.includes(classPart);
    
    // If any of the key fields are empty or placeholder, skip this period
    if (emptyValues.includes(classPart) || 
        emptyValues.includes(subject) || 
        emptyValues.includes(teacherCode)) {
      return true;
    }
    
    // For admin types, TBC is acceptable for room
    if (emptyValues.includes(room) || (room === 'TBC' && !isAdminType)) {
      return true;
    }
    
    // Check for single character teacher codes that might be placeholders
    if (teacherCode && teacherCode.length === 1 && /^[A-Z]$/.test(teacherCode)) {
      // This might be a placeholder teacher code, but let's be more specific
      // Only skip if other fields are also empty/placeholder
      if (emptyValues.includes(classPart) || emptyValues.includes(subject) || emptyValues.includes(room)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if a period is empty and should be skipped
   * @param {Object} period - Period object
   * @returns {boolean} Whether this period is empty and should be skipped
   */
  isPeriodEmpty(period) {
    // Check if period has no meaningful content
    if (!period.classes || period.classes.length === 0) {
      return true;
    }
    
    if (!period.subject || period.subject.trim() === '' || period.subject === 'Unknown Subject') {
      return true;
    }
    
    if (!period.teachers || period.teachers.length === 0) {
      return true;
    }
    
    // Check for placeholder content
    const emptyValues = ['', '""', 'Unknown', 'Unknown Room', 'TBC', 'N/A', '核心科'];
    
    // If all classes are empty/placeholder
    if (period.classes.every(cls => emptyValues.includes(cls))) {
      return true;
    }
    
    // If subject is empty/placeholder
    if (emptyValues.includes(period.subject)) {
      return true;
    }
    
    // If all teachers are empty/placeholder
    if (period.teachers.every(teacher => emptyValues.includes(teacher))) {
      return true;
    }
    
    return false;
  }

  /**
   * Detect class roles from file content
   * @param {string} content - File content
   * @returns {Object} Class roles information
   */
  detectClassRoles(content) {
    const roles = {
      isClassMaster: false,
      isViceClassMaster: false,
      classes: []
    };

    // Look for class role indicators
    const classMasterPatterns = [
      /Class Master/i,
      /班主任/i,
      /Class Master:\s*(.+)/i,
      /班主任:\s*(.+)/i
    ];

    const viceClassMasterPatterns = [
      /Vice Class Master/i,
      /副班主任/i,
      /Vice Class Master:\s*(.+)/i,
      /副班主任:\s*(.+)/i
    ];

    // Check for Class Master
    for (const pattern of classMasterPatterns) {
      const match = content.match(pattern);
      if (match) {
        roles.isClassMaster = true;
        if (match[1]) {
          roles.classes.push(match[1].trim());
        }
        break;
      }
    }

    // Check for Vice Class Master
    for (const pattern of viceClassMasterPatterns) {
      const match = content.match(pattern);
      if (match) {
        roles.isViceClassMaster = true;
        if (match[1]) {
          roles.classes.push(match[1].trim());
        }
        break;
      }
    }

    // Extract class information from content
    const classMatches = content.match(/\*\*Class:\*\*\s*(.+)/i);
    if (classMatches) {
      const classInfo = classMatches[1].trim();
      roles.classes.push(classInfo);
    }

    return roles;
  }

  /**
   * Enhanced class roles detection with home room extraction
   * @param {string} content - File content
   * @returns {Object} Enhanced class roles information
   */
  detectClassRolesEnhanced(content) {
    const roles = {
      isClassMaster: false,
      isViceClassMaster: false,
      classes: [],
      homeRoom: null
    };

    // Look for class role indicators with enhanced patterns
    const classMasterPatterns = [
      /Class Master/i,
      /班主任/i,
      /Class Master:\s*(.+)/i,
      /班主任:\s*(.+)/i
    ];

    const viceClassMasterPatterns = [
      /Vice Class Master/i,
      /副班主任/i,
      /Vice Class Master:\s*(.+)/i,
      /副班主任:\s*(.+)/i
    ];

    // Check for Class Master
    for (const pattern of classMasterPatterns) {
      const match = content.match(pattern);
      if (match) {
        roles.isClassMaster = true;
        if (match[1]) {
          roles.classes.push(match[1].trim());
        }
        break;
      }
    }

    // Check for Vice Class Master with enhanced parsing
    for (const pattern of viceClassMasterPatterns) {
      const match = content.match(pattern);
      if (match) {
        roles.isViceClassMaster = true;
        if (match[1]) {
          roles.classes.push(match[1].trim());
        }
        break;
      }
    }

    // Enhanced parsing for class and home room information
    // Look for patterns like "2D (from Teacher Assignment)" and "Home Room: 431"
    const classAssignmentMatch = content.match(/\*\*Vice Class Master \(副班主任\):\*\*\s*(\w+)\s*\(from Teacher Assignment\)/i);
    if (classAssignmentMatch) {
      roles.classes.push(classAssignmentMatch[1].trim());
    }

    // Also check for Class Master patterns
    const classMasterMatch = content.match(/\*\*Class Master \(班主任\):\*\*\s*(\w+)\s*\(from Teacher Assignment\)/i);
    if (classMasterMatch) {
      roles.classes.push(classMasterMatch[1].trim());
    }

    // Also check for simpler patterns like "Class Master: 6D"
    const simpleClassMasterMatch = content.match(/Class Master:\s*(\w+)/i);
    if (simpleClassMasterMatch) {
      roles.classes.push(simpleClassMasterMatch[1].trim());
    }

    // Check for patterns like "**Class Master (班主任):** 6D"
    const classMasterPattern2 = content.match(/\*\*Class Master \(班主任\):\*\*\s*(\w+)/i);
    if (classMasterPattern2) {
      roles.classes.push(classMasterPattern2[1].trim());
    }

    // Extract home room information with multiple patterns
    const homeRoomPatterns = [
      /\*\*\*This class Home Room:\s*(\w+)\s*\(from Teacher Assignment\)/i,
      /\*\*This class Home Room:\s*(\w+)\s*\(from Teacher Assignment\)/i,
      /This class Home Room:\s*(\w+)\s*\(from Teacher Assignment\)/i,
      /Home Room:\s*(\w+)/i
    ];
    
    for (const pattern of homeRoomPatterns) {
      const homeRoomMatch = content.match(pattern);
      if (homeRoomMatch) {
        roles.homeRoom = homeRoomMatch[1].trim();
        console.log(`🏠 Home room detected: "${roles.homeRoom}" using pattern: ${pattern}`);
        break;
      }
    }

    // Extract class information from content
    const classMatches = content.match(/\*\*Class:\*\*\s*(.+)/i);
    if (classMatches) {
      const classInfo = classMatches[1].trim();
      roles.classes.push(classInfo);
    }

    console.log(`🏠 Enhanced class roles detected:`, roles);
    return roles;
  }

  /**
   * Parse content for a specific day
   * @param {string} dayContent - Day content from file
   * @param {string} dayName - Day name (for Friday special case)
   * @param {Object} classRoles - Class roles information
   * @param {string} defaultTeacher - Default teacher name from file
   * @returns {Array} Array of period data
   */
  parseDayContent(dayContent, dayName, classRoles = null, defaultTeacher = null) {
    const periods = [];
    // Updated regex to match the actual format: "- Period 1 (8:10-8:45): 3D 綜合人文, Room G03"
    const periodMatches = dayContent.matchAll(/- Period (\d+) \(([^)]+)\): (.+)/g);
    
    for (const match of periodMatches) {
      const periodNumber = parseInt(match[1]);
      const timeRange = match[2];
      const description = match[3].trim();
      
      console.log(`📅 Parsing period ${periodNumber}: "${description}"`);
      
      // Get period info with debugging
      const periodInfo = this.getPeriodInfo(periodNumber, dayName);
      console.log(`📊 Period info for ${periodNumber}:`, periodInfo);
      
      // Parse the description to extract classes, subjects, teachers, and rooms
      const parsedEntries = this.parsePeriodDescription(description, periodNumber, timeRange, dayName, defaultTeacher);
      periods.push(...parsedEntries);
    }
    
    // Add 班主任節 periods for Class Master and Vice Class Master
    if (classRoles && (classRoles.isClassMaster || classRoles.isViceClassMaster)) {
      console.log(`🎭 Adding 班主任節 periods for ${classRoles.isClassMaster ? 'Class Master' : 'Vice Class Master'}`);
      
      // Add period_sequence=1 (first 班主任節)
      const firstHomeroomPeriod = {
        period: 0, // Special period
        period_sequence: 1,
        period_node: '班主任節',
        timeRange: '8:00-8:10',
        classes: classRoles.classes,
        subject: '班主任節',
        rooms: [],
        teachers: [],
        type: 'special',
        isClassMaster: classRoles.isClassMaster,
        isViceClassMaster: classRoles.isViceClassMaster
      };
      periods.unshift(firstHomeroomPeriod); // Add to beginning
      
      // Add period_sequence=15 (last 班主任節)
      const lastHomeroomPeriod = {
        period: 0, // Special period
        period_sequence: 15,
        period_node: '班主任節',
        timeRange: '3:35-3:50',
        classes: classRoles.classes,
        subject: '班主任節',
        rooms: [],
        teachers: [],
        type: 'special',
        isClassMaster: classRoles.isClassMaster,
        isViceClassMaster: classRoles.isViceClassMaster
      };
      periods.push(lastHomeroomPeriod); // Add to end
      
      console.log(`✅ Added 班主任節 periods: sequence 1 and 15`);
    }
    
    return periods;
  }

  /**
   * Parse period description with multi-class/teacher/room support
   * @param {string} description - Period description
   * @param {number} periodNumber - Period number
   * @param {string} timeRange - Time range
   * @param {string} dayName - Day name
   * @param {string} defaultTeacher - Default teacher name from file
   * @returns {Array} Array of parsed entries
   */
  parsePeriodDescription(description, periodNumber, timeRange, dayName = null, defaultTeacher = null) {
    const entries = [];
    
    // Get period sequence and node from setup data
    const periodInfo = this.getPeriodInfo(periodNumber, dayName);
    
    console.log(`Parsing description: "${description}"`);
    
    // Check for empty periods first - these should be skipped entirely
    if (!description || description.trim() === '' || description.trim() === '""') {
      console.log(`⏭️ Empty period detected - skipping period ${periodNumber} (no data to save)`);
      return entries; // Return empty array - no entries to create
    }
    
    // Handle different description formats
    // Format 1: "3D 綜合人文, Room G03" (class subject, Room room)
    // Format 2: "3B/3D 音樂, Room 606" (class1/class2 subject, Room room)
    // Format 3: "早會" (special periods)
    // Format 4: "S1 公經社同儕" (class subject - no room specified)
    
    // FLEXIBLE RULE: Check if this period is "barred" (restricted) by settings
    const isBarredPeriod = this.isBarredPeriodBySettings(periodInfo.period_sequence, periodInfo.period_node);
    
    if (isBarredPeriod) {
      // This period is "barred" - only allow access if it matches settings exactly
      const validatedDescription = this.validateBarredPeriodDescription(description, periodInfo.period_node);
      
      entries.push({
        period: periodNumber,
        period_sequence: periodInfo.period_sequence,
        period_node: periodInfo.period_node,
        timeRange: timeRange,
        classes: [],
        subject: validatedDescription,
        rooms: [],
        teachers: [],
        type: 'special'
      });
      return entries;
    }
    
    // If not barred, welcome any event that occurs in this period
    // This allows flexible content for regular periods (Period 1, 2, 3, etc.)
    // Continue with normal parsing for regular periods
    
    // Handle comma-separated format: "Class Subject, Room XXX Teacher"
    if (description.includes(',')) {
      const parts = description.split(',').map(part => part.trim());
      
      if (parts.length === 2) {
        // Format: "Class Subject, Room XXX Teacher" or "Class Subject, Room XXX"
        const classSubjectPart = parts[0];
        const roomTeacherPart = parts[1];
        
        // Extract room and teacher from the second part
        let room = '';
        let teacher = '';
        
        // Check if second part starts with "Room"
        if (roomTeacherPart.startsWith('Room ')) {
          const roomTeacherMatch = roomTeacherPart.match(/Room\s+([A-Za-z0-9\/\s]+?)(?:\s+([A-Za-z\u4e00-\u9fff\s]+))?$/);
          if (roomTeacherMatch) {
            room = roomTeacherMatch[1].trim();
            if (roomTeacherMatch[2]) {
              teacher = roomTeacherMatch[2].trim();
            }
          }
        } else {
          // Second part is just teacher name
          teacher = roomTeacherPart;
        }
        
        // Parse class and subject from first part
        const classSubjectParts = classSubjectPart.split(/\s+/).filter(part => part.length > 0);
        let classPart = '';
        let subject = '';
        
        if (classSubjectParts.length === 1) {
          // Only one part - could be class or subject
          const singlePart = classSubjectParts[0];
          if (/^[0-9]+[A-Za-z]+$/.test(singlePart)) {
            classPart = singlePart;
            subject = 'Unknown Subject';
          } else {
            subject = singlePart;
            classPart = 'Unknown Class';
          }
        } else {
          // Multiple parts - first is class, rest is subject
          classPart = classSubjectParts[0];
          subject = classSubjectParts.slice(1).join(' ');
        }
        
        // Handle multiple classes (e.g., "3B/3D")
        const classes = classPart.includes('/') ? classPart.split('/') : [classPart];
        
        // Create entries for each class
        for (const classItem of classes) {
          const entry = {
            period: periodNumber,
            period_sequence: periodInfo.period_sequence,
            period_node: periodInfo.period_node,
            timeRange: timeRange,
            classes: [classItem],
            subject: subject,
            rooms: room ? [room] : ['Unknown Room'],
            teachers: teacher ? [teacher] : [defaultTeacher || 'Unknown Teacher'],
            type: 'core'
          };
          
          console.log(`🔍 Created entry from comma-separated format:`, entry);
          entries.push(entry);
        }
        
        return entries;
      } else {
        // Multiple activities - parse each separately
        for (const activity of parts) {
          if (activity.trim()) {
            const activityEntries = this.parsePeriodDescription(activity, periodNumber, timeRange, dayName, defaultTeacher);
            entries.push(...activityEntries);
          }
        }
        return entries;
      }
    }

    // Parse the actual format: "Class Subject, Room YYY Teacher" or "Class Subject"
    // Examples: "S1 中文同儕", "4X1 中國歷史, Room 706", "1D 中國語文, Room 133/333 駱紫薇"
    
    // Enhanced extraction of room and teacher information
    // Pattern 1: ", Room XXX TeacherName" - room followed by teacher
    // Pattern 2: ", Room XXX" - room only
    // Pattern 3: "TeacherName" - teacher only (at end)
    // Pattern 4: "Room XXX TeacherName" - room and teacher without comma
    
    let room = '';
    let teacher = '';
    
    // Enhanced pattern matching for room and teacher extraction
    // Handle various formats including the specific patterns from the image
    
    // Pattern 1: ", Room XXX TeacherName" - most common format
    let roomMatch = description.match(/,\s*Room\s+([A-Za-z0-9\/\s]+?)\s+([A-Za-z\u4e00-\u9fff\s]+)$/);
    if (roomMatch) {
      room = roomMatch[1].trim();
      teacher = roomMatch[2].trim();
    } else {
      // Pattern 2: ", Room XXX" - room only
      roomMatch = description.match(/,\s*Room\s+([A-Za-z0-9\/\s]+)$/);
      if (roomMatch) {
        room = roomMatch[1].trim();
      } else {
        // Pattern 3: "Room XXX TeacherName" (without comma)
        roomMatch = description.match(/Room\s+([A-Za-z0-9\/\s]+?)\s+([A-Za-z\u4e00-\u9fff\s]+)$/);
        if (roomMatch) {
          room = roomMatch[1].trim();
          teacher = roomMatch[2].trim();
        } else {
          // Pattern 4: "Room XXX" (without comma)
          roomMatch = description.match(/Room\s+([A-Za-z0-9\/\s]+)$/);
          if (roomMatch) {
            room = roomMatch[1].trim();
          } else {
            // Pattern 5: Handle specific formats from the image
            // Check for single letter + Chinese text (e.g., "C 設計與科技")
            const teacherCodeMatch = description.match(/^([A-Z])\s+([\u4e00-\u9fff\s()]+)$/);
            if (teacherCodeMatch) {
              teacher = teacherCodeMatch[1].trim();
              // The subject will be extracted later
            } else {
              // Pattern 6: Just Chinese text (could be teacher name or subject)
              const chineseMatch = description.match(/^([\u4e00-\u9fff\s()]+)$/);
              if (chineseMatch) {
                const text = chineseMatch[1].trim();
                // Only treat as teacher if it's very short (1 character) and doesn't contain parentheses
                // 2+ character Chinese text is likely a subject name
                if (text.length === 1 && !text.includes('(')) {
                  teacher = text;
                }
                // Otherwise, it will be treated as subject
              } else {
                // Pattern 7: Just teacher name at the end (fallback)
                const teacherMatch = description.match(/([A-Za-z\u4e00-\u9fff\s]+)$/);
                if (teacherMatch) {
                  teacher = teacherMatch[1].trim();
                }
              }
            }
          }
        }
      }
    }
    
    console.log(`🔍 PARSING: "${description}"`);
    console.log(`🔍 Extracted room: "${room}"`);
    console.log(`🔍 Extracted teacher: "${teacher}"`);
    
    // Extract co-teachers if present
    const coTeacherMatch = description.match(/Co-teacher:\s*(.+)$/);
    const coTeachers = coTeacherMatch ? coTeacherMatch[1].split(',').map(t => t.trim()) : [];
    
    // Remove room and teacher parts to get class and subject
    let classSubjectPart = description;
    
    // Remove room patterns (with comma)
    classSubjectPart = classSubjectPart.replace(/,\s*Room\s+[A-Za-z0-9\/\s]+(?:\s+[A-Za-z\u4e00-\u9fff\s]+)?$/, '');
    
    // Remove room patterns (without comma)
    classSubjectPart = classSubjectPart.replace(/Room\s+[A-Za-z0-9\/\s]+(?:\s+[A-Za-z\u4e00-\u9fff\s]+)?$/, '');
    
    // Remove co-teacher patterns
    if (coTeacherMatch) {
      classSubjectPart = classSubjectPart.replace(/,\s*Co-teacher:.*$/, '');
    }
    
    // Handle special case: if we extracted a teacher code (single letter), remove it
    if (teacher && teacher.length === 1 && /^[A-Z]$/.test(teacher)) {
      // Remove the teacher code from the beginning
      classSubjectPart = classSubjectPart.replace(/^[A-Z]\s+/, '');
    }
    
    // Remove standalone teacher names at the end (but only if we haven't already extracted them)
    // Only do this if we haven't found a teacher through room extraction
    if (!teacher || teacher.length > 1) {
      // More specific pattern to avoid removing subject names
      classSubjectPart = classSubjectPart.replace(/\s+[A-Za-z]+$/, '');
    }
    
    console.log(`🔍 Class/Subject part after cleanup: "${classSubjectPart}"`);
    
    // Split by spaces to get class and subject
    const parts = classSubjectPart.split(/\s+/).filter(part => part.length > 0);
    console.log(`🔍 Parts after splitting:`, parts);
    
    let classPart = '';
    let subject = '';
    
    if (parts.length === 0) {
      console.warn(`Could not parse class and subject from: "${classSubjectPart}"`);
      return entries;
    } else if (parts.length === 1) {
      // Only one part - could be class or subject
      const singlePart = parts[0];
      // Check if it looks like a class (contains numbers and letters)
      if (/^[0-9]+[A-Za-z]+$/.test(singlePart)) {
        classPart = singlePart;
        subject = 'Unknown Subject';
      } else {
        // Likely a subject
        subject = singlePart;
        classPart = 'Unknown Class';
      }
    } else {
      // Multiple parts - first is class, rest is subject
      classPart = parts[0]; // e.g., "3D" or "3B/3D"
      subject = parts.slice(1).join(' '); // e.g., "綜合人文" or "音樂"
    }
    
    console.log(`🔍 Extracted class: "${classPart}"`);
    console.log(`🔍 Extracted subject: "${subject}"`);
    
    // Handle multiple classes (e.g., "3B/3D")
    const classes = classPart.includes('/') ? classPart.split('/') : [classPart];
    
    // Combine all teachers found
    const allTeachers = [...coTeachers];
    if (teacher) {
      allTeachers.push(teacher);
    }
    
    // If no teacher found, use default teacher from file or placeholder
    if (allTeachers.length === 0) {
      allTeachers.push(defaultTeacher || 'Unknown Teacher');
    }
    
    // If no room found, add a placeholder for manual update
    const allRooms = room ? [room] : ['Unknown Room'];
    
    // Create entries for each class
    for (const classItem of classes) {
      const entry = {
        period: periodNumber,
        period_sequence: periodInfo.period_sequence,
        period_node: periodInfo.period_node,
        timeRange: timeRange,
        classes: [classItem],
        subject: subject,
        rooms: allRooms,
        teachers: allTeachers,
        type: 'core'
      };
      
      console.log(`🔍 Created entry:`, entry);
      entries.push(entry);
    }

    console.log(`Created ${entries.length} entries for period ${periodNumber}`);
    return entries;
  }

  /**
   * Parse complex description with multiple classes, subjects, teachers, rooms
   * @param {string} description - Description to parse
   * @param {string} defaultTeacher - Default teacher name to use if none found
   * @returns {Object} Parsed data
   */
  parseComplexDescription(description, defaultTeacher = null) {
    // Split by spaces and analyze each part
    const parts = description.split(/\s+/).filter(part => part.length > 0);
    
    let classes = [];
    let subjects = [];
    let teachers = [];
    let rooms = [];
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      
      // Check if it's a class (contains numbers and letters, possibly with /)
      if (this.isClassPattern(part)) {
        classes = this.splitByDelimiter(part, ['/', ',']);
      }
      // Check if it's a room (numbers only, possibly with /)
      else if (this.isRoomPattern(part)) {
        rooms = this.splitByDelimiter(part, ['/', ',']);
      }
      // Check if it's a teacher (Chinese characters, possibly with /)
      else if (this.isTeacherPattern(part)) {
        teachers = this.splitByDelimiter(part, ['/', ',']);
      }
      // Otherwise, it's likely a subject
      else {
        subjects.push(part);
      }
    }
    
    // If no classes found, try to extract from the first part
    if (classes.length === 0 && parts.length > 0) {
      const firstPart = parts[0];
      if (this.isClassPattern(firstPart)) {
        classes = this.splitByDelimiter(firstPart, ['/', ',']);
        // Remove the first part from subjects if it was added
        subjects = subjects.filter(subject => subject !== firstPart);
      }
    }
    
    // If no teachers found, try to extract from the last part
    if (teachers.length === 0 && parts.length > 0) {
      const lastPart = parts[parts.length - 1];
      if (this.isTeacherPattern(lastPart)) {
        teachers = this.splitByDelimiter(lastPart, ['/', ',']);
        // Remove the last part from subjects if it was added
        subjects = subjects.filter(subject => subject !== lastPart);
      }
    }
    
    // If no rooms found, try to extract from middle parts
    if (rooms.length === 0 && parts.length > 1) {
      for (let i = 1; i < parts.length - 1; i++) {
        const part = parts[i];
        if (this.isRoomPattern(part)) {
          rooms = this.splitByDelimiter(part, ['/', ',']);
          // Remove this part from subjects if it was added
          subjects = subjects.filter(subject => subject !== part);
          break;
        }
      }
    }
    
    // Ensure we have at least one item in each category
    if (classes.length === 0) classes = ['UNKNOWN'];
    if (subjects.length === 0) subjects = ['UNKNOWN'];
    if (teachers.length === 0) teachers = [defaultTeacher || 'UNKNOWN'];
    if (rooms.length === 0) rooms = ['N/A'];
    
    return { classes, subjects, teachers, rooms };
  }

  /**
   * Check if a string matches class pattern (e.g., "4AB3", "3B/3D")
   */
  isClassPattern(str) {
    return /^[0-9]+[A-Za-z]+[0-9]*(\/[0-9]+[A-Za-z]+[0-9]*)*$/.test(str);
  }

  /**
   * Check if a string matches room pattern (e.g., "110", "112/510")
   */
  isRoomPattern(str) {
    return /^[0-9]+(\/[0-9]+)*$/.test(str);
  }

  /**
   * Check if a string matches teacher pattern (Chinese characters)
   */
  isTeacherPattern(str) {
    return /^[\u4e00-\u9fff]+(\/[\u4e00-\u9fff]+)*$/.test(str);
  }

  /**
   * Split string by delimiters
   */
  splitByDelimiter(str, delimiters) {
    let result = [str];
    for (const delimiter of delimiters) {
      result = result.flatMap(item => item.split(delimiter)).map(item => item.trim());
    }
    return result.filter(item => item.length > 0);
  }

  /**
   * Check if a period is "barred" (restricted) based on settings configuration
   * @param {number} periodSequence - Period sequence number
   * @param {string} periodNode - Period node from settings
   * @returns {boolean} Whether this period is barred/restricted
   */
  isBarredPeriodBySettings(periodSequence, periodNode) {
    // Check if the period_node from settings indicates this should be barred/restricted
    // These are periods that have specific rules and should not accept arbitrary content
    const barredPeriodNodes = ['班主任節', 'Recess', 'Lunch', '早會', 'Morning Assembly'];
    return barredPeriodNodes.includes(periodNode);
  }

  /**
   * Validate barred period description against settings configuration
   * @param {string} description - Description from file
   * @param {string} expectedPeriodNode - Expected period node from settings
   * @returns {string} Validated description for barred period
   */
  validateBarredPeriodDescription(description, expectedPeriodNode) {
    // For barred periods, use the expected period node from settings
    // This ensures only approved content can access this period
    console.log(`🔒 BARRED PERIOD: Expected "${expectedPeriodNode}", got "${description}"`);
    return expectedPeriodNode;
  }

  /**
   * Get period type based on description
   */
  getPeriodType(description) {
    if (description === '小息') return 'break';
    if (description === '午膳') return 'lunch';
    if (description === 'Empty') return 'empty';
    if (description === '班主任節') return 'homeroom';
    return 'core';
  }

  /**
   * Map period number from text file to period_sequence
   * Based on the 15-period structure:
   * Row 1: 班主任節 (8:00-8:10) - period_sequence: 1
   * Row 2: Period 1 (8:10-8:45) - period_sequence: 2
   * Row 3: Period 2 (8:45-9:20) - period_sequence: 3
   * Row 4: Period 3 (9:20-9:55) - period_sequence: 4
   * Row 5: Recess (9:55-10:15) - period_sequence: 5
   * Row 6: Period 4 (10:15-10:50) - period_sequence: 6
   * Row 7: Period 5 (10:50-11:25) - period_sequence: 7
   * Row 8: Recess (11:25-11:30) - period_sequence: 8
   * Row 9: Period 6 (11:30-12:05) - period_sequence: 9
   * Row 10: Period 7 (12:05-12:40) - period_sequence: 10
   * Row 11: Lunch (12:40-1:50) - period_sequence: 11
   * Row 12: Period 8 (1:50-2:25) - period_sequence: 12
   * Row 13: Period 9 (2:25-3:00) - period_sequence: 13
   * Row 14: Period 10 (3:00-3:35) - period_sequence: 14
   * Row 15: 班主任節 (3:35-3:50) - period_sequence: 15
   */
  mapPeriodNumberToSequence(periodNumber) {
    const periodMapping = {
      1: 2,   // Period 1 → period_sequence: 2
      2: 3,   // Period 2 → period_sequence: 3
      3: 4,   // Period 3 → period_sequence: 4
      4: 6,   // Period 4 → period_sequence: 6
      5: 7,   // Period 5 → period_sequence: 7
      6: 9,   // Period 6 → period_sequence: 9
      7: 10,  // Period 7 → period_sequence: 10
      8: 12,  // Period 8 → period_sequence: 12
      9: 13,  // Period 9 → period_sequence: 13
      10: 14  // Period 10 → period_sequence: 14
    };
    
    return periodMapping[periodNumber] || periodNumber;
  }

  /**
   * Get period information (sequence and node) from setup data
   * @param {number} periodNumber - Period number
   * @param {string} dayName - Day name (for Friday special case)
   * @returns {Object} Period info with sequence and node
   */
  getPeriodInfo(periodNumber, dayName = null) {
    console.log(`🔍 Looking for period info for period ${periodNumber}`);
    console.log(`📋 Available period patterns:`, this.periodPatterns);
    
    // Ensure periodNumber is a valid number
    const validPeriodNumber = parseInt(periodNumber);
    if (isNaN(validPeriodNumber) || validPeriodNumber < 1) {
      throw new Error(`Invalid period number: ${periodNumber}. Please configure timetable settings first.`);
    }
    
    // Map period number to period_sequence
    const periodSequence = this.mapPeriodNumberToSequence(validPeriodNumber);
    console.log(`📊 Mapped period ${validPeriodNumber} to period_sequence ${periodSequence}`);
    
    // Find the pattern for this period_sequence
    let pattern = this.periodPatterns.find(p => p.period_sequence === periodSequence);
    
    if (pattern) {
      console.log(`✅ Found pattern for period ${validPeriodNumber} (sequence ${periodSequence}):`, pattern);
      return {
        period_sequence: pattern.period_sequence,
        period_node: pattern.period_node
      };
    }
    
    // STRICT RULE: NO FALLBACK - Use settings configuration only
    console.error(`❌ NO PATTERN FOUND for period ${validPeriodNumber} (sequence ${periodSequence})`);
    console.error(`❌ Settings configuration is incomplete!`);
    console.error(`❌ Available patterns:`, this.periodPatterns.map(p => `${p.period_sequence}: ${p.period_node}`));
    console.error(`❌ This means the settings page needs to be configured properly!`);
    
    // Return error state - don't create fallback
    throw new Error(`No configuration found for period ${validPeriodNumber} (sequence ${periodSequence}). Please configure the timetable settings first.`);
  }

  /**
   * Find pattern by time slot mapping
   * @param {number} periodNumber - Period number
   * @returns {Object|null} Pattern or null
   */
  findPatternByTimeSlot(periodNumber) {
    // Updated period to time slot mappings for 15-period structure
    const periodTimeMappings = {
      1: '8:00-8:10',   // 班主任節
      2: '8:10-8:45',   // Period 1
      3: '8:45-9:20',   // Period 2
      4: '9:20-9:55',   // Period 3
      5: '9:55-10:15',  // Recess
      6: '10:15-10:50', // Period 4
      7: '10:50-11:25', // Period 5
      8: '11:25-11:30', // Recess
      9: '11:30-12:05', // Period 6
      10: '12:05-12:40', // Period 7
      11: '12:40-1:50',  // Lunch
      12: '1:50-2:25',   // Period 8
      13: '2:25-3:00',   // Period 9
      14: '3:00-3:35',   // Period 10
      15: '3:35-3:50'    // 班主任節
    };
    
    const expectedTimeSlot = periodTimeMappings[periodNumber];
    if (expectedTimeSlot) {
      return this.periodPatterns.find(p => p.time_slot === expectedTimeSlot);
    }
    
    return null;
  }

  /**
   * Convert parsed data to UI format
   */
  convertToUIFormat(parsedData) {
    // Get the default teacher name from parsed data
    const defaultTeacher = parsedData.teacherName || null;
    console.log(`👨‍🏫 convertToUIFormat using default teacher: ${defaultTeacher}`);
    const uiData = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: []
    };
    
    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday',
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };
    
    // Ensure parsedData and schedule exist
    if (!parsedData || !parsedData.schedule) {
      console.warn('Invalid parsed data structure:', parsedData);
      return uiData;
    }
    
    for (const [dayName, periods] of Object.entries(parsedData.schedule)) {
      const dayKey = dayMapping[dayName];
      if (!dayKey) continue;
      
      // Ensure periods is an array
      if (!Array.isArray(periods)) {
        console.warn(`Invalid periods data for ${dayName}:`, periods);
        continue;
      }
      
      for (const period of periods) {
        // Ensure period has required properties
        if (!period || typeof period !== 'object') {
          console.warn(`Invalid period data:`, period);
          continue;
        }
        
        // Skip periods that are empty or have no meaningful content
        if (this.isPeriodEmpty(period)) {
          console.log(`⏭️ Skipping empty period ${period.period} - no UI entry created`);
          continue;
        }
        
        const timeInfo = this.getTimeSlot(period.period);
        
        try {
          const entries = this.createUIEntries(period, timeInfo, defaultTeacher);
          if (Array.isArray(entries) && entries.length > 0) {
            // Group entries by period_sequence instead of just pushing them
            for (const entry of entries) {
              const periodSequence = entry.period_sequence;
              if (periodSequence && periodSequence >= 1 && periodSequence <= 15) {
                // Ensure the array is long enough
                while (uiData[dayKey].length < periodSequence) {
                  uiData[dayKey].push(null);
                }
                // Place the entry at the correct period_sequence index (0-based)
                uiData[dayKey][periodSequence - 1] = entry;
              } else {
                // Fallback: push to end if period_sequence is invalid
                uiData[dayKey].push(entry);
              }
            }
          } else {
            console.log(`⏭️ No UI entries created for period ${period.period} - period will appear as blank`);
          }
        } catch (error) {
          console.error(`Error creating UI entries for period:`, error, period);
        }
      }
    }
    
    return uiData;
  }

  /**
   * Get time slot for period
   */
  getTimeSlot(periodNumber) {
    const pattern = this.periodPatterns.find(p => p.period_sequence === periodNumber);
    if (pattern) {
      return pattern.time_slot;
    }
    
    // Try to find by period_node containing the period number
    const nodePattern = this.periodPatterns.find(p => 
      p.period_node && p.period_node.includes(`Period ${periodNumber}`)
    );
    if (nodePattern) {
      return nodePattern.time_slot;
    }
    
    // Try time-based mapping
    const timeBasedPattern = this.findPatternByTimeSlot(periodNumber);
    if (timeBasedPattern) {
      return timeBasedPattern.time_slot;
    }
    
    return 'TBD';
  }

  /**
   * Get time sequence for period
   */
  getTimeSequence(periodNumber) {
    const pattern = this.periodPatterns.find(p => p.period_sequence === periodNumber);
    if (pattern) {
      return pattern.period_sequence;
    }
    
    // Try to find by period_node containing the period number
    const nodePattern = this.periodPatterns.find(p => 
      p.period_node && p.period_node.includes(`Period ${periodNumber}`)
    );
    if (nodePattern) {
      return nodePattern.period_sequence;
    }
    
    // Try time-based mapping
    const timeBasedPattern = this.findPatternByTimeSlot(periodNumber);
    if (timeBasedPattern) {
      return timeBasedPattern.period_sequence;
    }
    
    return periodNumber; // Fallback to period number
  }

  /**
   * Create UI entries for a period
   */
  createUIEntries(period, timeInfo, defaultTeacher = null) {
    const entries = [];
    
    // Ensure all arrays exist and have at least one element
    const classes = period.classes && period.classes.length > 0 ? period.classes : ['UNKNOWN'];
    const subject = period.subject || 'UNKNOWN';
    const teachers = period.teachers && period.teachers.length > 0 ? period.teachers : [defaultTeacher || 'UNKNOWN'];
    const rooms = period.rooms && period.rooms.length > 0 ? period.rooms : ['N/A'];
    
    // Get time sequence for this period
    const timeSequence = this.getTimeSequence(period.period);
    
    // Create entries for each class
    for (let i = 0; i < classes.length; i++) {
      const classItem = classes[i];
      const room = rooms[i] || rooms[0] || 'N/A';
      const teacherCode = teachers[i] || teachers[0] || defaultTeacher || 'UNKNOWN';
      const teacher = this.mapTeacherCodeToNameSync(teacherCode);
      
      // Ensure period_sequence and period_node are properly set with fallbacks
      const periodSequence = period.period_sequence !== undefined && period.period_sequence !== null 
        ? period.period_sequence 
        : (period.period !== undefined && period.period !== null ? period.period : 1);
      
      const periodNode = period.period_node !== undefined && period.period_node !== null && period.period_node !== ''
        ? period.period_node 
        : `Period ${periodSequence}`;
      
      // Handle co-teachers from enhanced format and map to full names
      const coTeachers = (period.co_teachers || []).map(code => this.mapTeacherCodeToNameSync(code));
      const allTeachers = [teacher, ...coTeachers];
      
      const uiEntry = {
        time: timeInfo,
        period_number: period.period,
        period_sequence: periodSequence,
        period_node: periodNode,
        time_sequence: timeSequence, // Add time sequence
        subject: subject,
        class_id: classItem,
        classroom: room,
        teacher_code: teacher, // Store teacher code instead of name
        teacher: teacher, // For TimetableGrid compatibility
        teacher_name: teacher, // For backward compatibility, also store as teacher_name
        type: period.type || period.subject_type || 'core',
        subject_type: period.subject_type || period.type || 'core', // Add subject_type field
        week_type: 'regular',
        elective_group: period.elective_group || null, // Add elective_group field
        co_teachers: coTeachers, // Add co_teachers field
        notes: allTeachers.length > 1 ? `Co-teachers: ${allTeachers.join(', ')}` : '',
        id: `${period.period}_${i}_${Date.now()}`
      };
      
      console.log(`Creating UI entry for period ${period.period}:`, {
        period_sequence: uiEntry.period_sequence,
        period_node: uiEntry.period_node,
        teacher_code: uiEntry.teacher_code,
        subject_type: uiEntry.subject_type,
        elective_group: uiEntry.elective_group,
        co_teachers: uiEntry.co_teachers,
        period: period
      });
      
      entries.push(uiEntry);
    }
    
    return entries;
  }
}

export default EnhancedTimetableParser;
